// Global hash-triggered welcome promos.
// Direct links: any theme page + one of the hashes below. Old /discount links still work.
// Shopify owns discount eligibility/minimums. Code presence is not eligibility.
// Existing gifts retain qty=1/remove-without-code rules; Mystery Wooble stays unrestricted.
// Mystery Accessory is add-only; Shopify applies its automatic discount when eligible.
// No global cart listeners. The UI observer is scoped to SlideCart.
(() => {
  if (window.__CHARM_PROMO_ENFORCE_INIT__) return;
  window.__CHARM_PROMO_ENFORCE_INIT__ = true;

  const CHARM_PROMOS = [
    {
      variantId: '46715440890022',
      requiredCode: 'CHARMEDTOMEETYOU',
      hash: '#promo_charm_bestsellers',
      sessionFlag: 'promo_charm_bestsellers_applied',
    },
    {
      variantId: '43389165732006',
      requiredCode: 'KEEPYOURTINUP',
      hash: '#promo_keepyourtinup',
      sessionFlag: 'promo_keepyourtinup_applied',
    },
    {
      variantId: '56860165537958',
      requiredCode: 'KEYSYPEASY',
      hash: '#promo_keychain',
      sessionFlag: 'promo_keychain_applied',
    },
    {
      variantId: '44416648118438', // Mystery Wooble — SKU 22MY
      requiredCode: 'RET-MYSTERYFRIEND',
      hash: '#promo_mysteryfriend',
      sessionFlag: 'promo_mysteryfriend_applied',
      lockQuantity: false,
      removeWithoutCode: false,
    },
    {
      variantId: '45922275426470', // Mystery Accessory
      requiredCode: null, // Automatic Shopify discount; do not submit a code.
      hash: '#promo_mysteryaccessory',
      sessionFlag: 'promo_mysteryaccessory_applied',
      lockQuantity: false,
      removeWithoutCode: false,
    },
  ];
  const PROMO_BY_VARIANT = new Map(CHARM_PROMOS.map((p) => [p.variantId, p]));
  const LOCKED_PROMOS = CHARM_PROMOS.filter((p) => p.lockQuantity !== false);
  const root = (window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
  const normalizeCode = (code) =>
    String(code || '')
      .trim()
      .toUpperCase();
  const hasCode = (cart, code) =>
    !!normalizeCode(code) && (cart.discount_codes || []).some((d) => normalizeCode(d.code) === normalizeCode(code));
  const hasItem = (cart, promo) => (cart.items || []).some((i) => String(i.variant_id) === promo.variantId);
  let memoryContext = false;

  function readSession(key) {
    try {
      return !!sessionStorage.getItem(key);
    } catch (_) {
      return false;
    }
  }
  function markSession(promo) {
    memoryContext = true;
    try {
      sessionStorage.setItem(promo.sessionFlag, 'true');
    } catch (_) {
      /* Storage can be unavailable. */
    }
  }
  function getActiveCharmLanding() {
    return CHARM_PROMOS.find((p) => p.hash === window.location.hash) || null;
  }
  function cleanCharmPromoHash(hash) {
    if (window.location.hash === hash && window.history?.replaceState) {
      window.history.replaceState(window.history.state, '', window.location.pathname + window.location.search);
    }
  }
  function matchesCharmPromoCode(raw) {
    let decoded = raw || '';
    try {
      decoded = decodeURIComponent(decoded);
    } catch (_) {
      /* Compare safely as-is. */
    }
    return decoded
      .split(',')
      .some((code) =>
        CHARM_PROMOS.some(
          (p) => normalizeCode(p.requiredCode) && normalizeCode(code) === normalizeCode(p.requiredCode),
        ),
      );
  }
  function isCharmPromoContext() {
    if (getActiveCharmLanding() || memoryContext || CHARM_PROMOS.some((p) => readSession(p.sessionFlag))) return true;
    const match = window.location.pathname.match(/\/discount\/([^/]+)/);
    if (match && matchesCharmPromoCode(match[1])) return true;
    const params = new URLSearchParams(window.location.search);
    return ['discount', 'auto-discount'].some((key) => matchesCharmPromoCode(params.get(key)));
  }

  // Reads are deduplicated; a forced read waits out an older request before fetching.
  let cartInflight = null;
  let cartCache = null;
  let cartCacheAt = 0;
  async function requestCart(path, body) {
    const response = await fetch(root + path, {
      credentials: 'same-origin',
      cache: 'no-store',
      ...(body === undefined
        ? {}
        : {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
          }),
    });
    if (!response.ok) throw new Error(`${path} failed (${response.status})`);
    return response.json();
  }
  async function getCart({ bust = false } = {}) {
    if (bust && cartInflight) await cartInflight;
    if (!bust && cartCache && Date.now() - cartCacheAt < 350) return cartCache;
    if (cartInflight) return cartInflight;
    cartInflight = requestCart('cart.js')
      .then((cart) => {
        if (!Array.isArray(cart.items)) throw new Error('Invalid cart response');
        cartCache = cart;
        cartCacheAt = Date.now();
        return cart;
      })
      .finally(() => {
        cartInflight = null;
      });
    return cartInflight;
  }
  async function mutateCart(path, body) {
    if (cartInflight) await cartInflight;
    cartCache = null;
    try {
      return await requestCart(path, body);
    } finally {
      cartCache = null;
    }
  }
  function refreshSlideCart() {
    try {
      const result = window.SLIDECART_UPDATE?.();
      result?.catch?.((error) => console.warn('[CharmPromo] SlideCart refresh failed', error));
    } catch (error) {
      console.warn('[CharmPromo] SlideCart refresh failed', error);
    }
  }

  // No session-based early return: a new explicit click may re-add a removed item.
  // The cart itself prevents duplicates and preserves existing Mystery quantities.
  async function maybeAutoAddCharmPromoFromLanding(promo) {
    const requiredCode = normalizeCode(promo.requiredCode);
    let addedHere = false;
    try {
      let cart = await getCart({ bust: true });
      if (!hasItem(cart, promo)) {
        await mutateCart('cart/add.js', { items: [{ id: promo.variantId, quantity: 1 }] });
        addedHere = true;
      }
      cart = await getCart({ bust: true });
      // Automatic-discount promos only add the item; leave existing codes untouched.
      if (requiredCode) {
        // Include existing codes so applying this offer doesn't intentionally clear them.
        const codes = [
          ...new Set([...(cart.discount_codes || []).map((d) => normalizeCode(d.code)), requiredCode].filter(Boolean)),
        ];
        await mutateCart('cart/update.js', { discount: codes.join(',') });
      }

      // Verify the item and, for code-based promos only, code registration.
      // Automatic discount eligibility may be met later as the customer shops.
      for (let attempt = 0; attempt < 5; attempt++) {
        cart = await getCart({ bust: true });
        if (hasItem(cart, promo) && (!requiredCode || hasCode(cart, requiredCode))) {
          markSession(promo);
          cleanCharmPromoHash(promo.hash);
          return true;
        }
        if (attempt < 4) await sleep(250);
      }
      throw new Error(`Could not verify promo item${requiredCode ? ` and ${requiredCode}` : ''}`);
    } catch (error) {
      // Avoid leaving a newly auto-added, full-price item after a failed code request.
      // Never roll back a pre-existing item or remove a code registered despite a network error.
      if (addedHere && requiredCode) {
        try {
          const cart = await getCart({ bust: true });
          if (!hasCode(cart, promo.requiredCode)) {
            const lines = cart.items.filter((i) => String(i.variant_id) === promo.variantId);
            if (lines.length === 1 && lines[0].quantity === 1) {
              await mutateCart('cart/change.js', { id: lines[0].key, quantity: 0 });
            }
          }
        } catch (rollbackError) {
          console.error('[CharmPromo] Could not verify/undo failed promo add', rollbackError);
        }
      }
      // Keep the hash so refresh can retry. Do not retry an ambiguous add automatically.
      console.error('[CharmPromo] Promo activation failed; reload to retry', error);
      return false;
    } finally {
      refreshSlideCart();
    }
  }

  function hideAndDisableQtyUIForVariant(variantId) {
    const vid = String(variantId);
    const cartRoot = document.querySelector('#slidecarthq') || document.querySelector('.slidecarthq') || document;
    cartRoot
      .querySelectorAll(`[data-variant-id="${vid}"], [data-variant="${vid}"], [data-variantid="${vid}"]`)
      .forEach((line) => {
        const wrapper =
          line.querySelector('.quantity-selector') ||
          line.querySelector('.cart-item__quantity') ||
          line.querySelector('.line-item__quantity');
        const dec = line.querySelector('button[aria-label="decrease quantity"]');
        const inc = line.querySelector('button[aria-label="increase quantity"]');
        const input = line.querySelector(
          ".quantity-selector input[type='text'], .quantity-selector input[type='number']",
        );
        [dec, inc].filter(Boolean).forEach((button) => {
          button.disabled = true;
          button.setAttribute('aria-disabled', 'true');
          button.style.display = 'none';
          button.classList.add('charm-promo-qty-hidden');
        });
        if (input) {
          input.readOnly = true;
          input.value = '1';
          input.style.display = 'none';
          input.classList.add('charm-promo-qty-hidden');
        }
        if (wrapper) {
          wrapper.style.display = 'none';
          wrapper.classList.add('charm-promo-qty-hidden');
        }
      });
    document.querySelectorAll('.cart__qty-input').forEach((input) => {
      const match = input.id && input.id.match(/updates_large_(\d+)/);
      if (!match || match[1] !== vid) return;
      input.readOnly = true;
      input.value = '1';
      input.style.display = 'none';
      input.classList.add('charm-promo-qty-hidden');
      const wrap = input.closest('.cart__qty') || input.closest('.quantity') || input.closest('.quantity-selector');
      if (wrap) {
        wrap.style.display = 'none';
        wrap.classList.add('charm-promo-qty-hidden');
      }
    });
  }
  let uiObserver = null;
  let observerTask = null;
  function hideLockedQtyUI() {
    LOCKED_PROMOS.forEach((p) => hideAndDisableQtyUIForVariant(p.variantId));
  }
  function tryAttachObserverSoon() {
    if (uiObserver || observerTask) return;
    observerTask = (async () => {
      hideLockedQtyUI();
      for (let attempt = 0; attempt < 8; attempt++) {
        const target = document.querySelector('#slidecarthq') || document.querySelector('.slidecarthq');
        if (target) {
          uiObserver = new MutationObserver(hideLockedQtyUI);
          uiObserver.observe(target, { childList: true, subtree: true });
          hideLockedQtyUI();
          return;
        }
        await sleep(250);
      }
    })()
      .catch((error) => console.warn('[CharmPromo] Quantity UI update failed', error))
      .finally(() => {
        observerTask = null;
      });
  }

  async function enforcePromoRules({ longWait = false } = {}) {
    let cart = await getCart({ bust: true });
    // Wait for the specific codes required by restricted gifts, not just any code.
    for (let attempt = 0; attempt < (longWait ? 16 : 5); attempt++) {
      const missingCode = LOCKED_PROMOS.some(
        (p) =>
          normalizeCode(p.requiredCode) &&
          p.removeWithoutCode !== false &&
          hasItem(cart, p) &&
          !hasCode(cart, p.requiredCode),
      );
      if (!missingCode) break;
      await sleep(longWait ? 400 : 250);
      cart = await getCart({ bust: true });
    }
    if (!cart.items.some((i) => PROMO_BY_VARIANT.has(String(i.variant_id)))) return;
    tryAttachObserverSoon();
    let changed = false;
    // Re-read before each change: discount changes can change Shopify line keys.
    const promosToEnforce = CHARM_PROMOS.filter(
      (p) =>
        hasItem(cart, p) &&
        (p.lockQuantity !== false || (normalizeCode(p.requiredCode) && p.removeWithoutCode !== false)),
    );
    for (const promo of promosToEnforce) {
      cart = await getCart({ bust: true });
      const lines = cart.items.filter((i) => String(i.variant_id) === promo.variantId);
      for (const original of lines) {
        const fresh = await getCart({ bust: true });
        const item = fresh.items.find((i) => i.key === original.key);
        if (!item) continue;
        const remove =
          !!normalizeCode(promo.requiredCode) &&
          promo.removeWithoutCode !== false &&
          !hasCode(fresh, promo.requiredCode);
        if (remove || (promo.lockQuantity !== false && item.quantity !== 1)) {
          await mutateCart('cart/change.js', { id: item.key, quantity: remove ? 0 : 1 });
          changed = true;
        }
        if (!remove && promo.lockQuantity !== false) hideAndDisableQtyUIForVariant(promo.variantId);
      }
    }
    if (changed) refreshSlideCart();
  }

  // Serialize activation + enforcement so this file never removes a gift mid-activation.
  let running = false;
  let queued = false;
  let followupTimer = null;
  async function runPromoFlow() {
    if (running) {
      queued = true;
      return;
    }
    if (!isCharmPromoContext()) return;
    running = true;
    clearTimeout(followupTimer);
    try {
      const promo = getActiveCharmLanding();
      tryAttachObserverSoon();
      if (promo && !(await maybeAutoAddCharmPromoFromLanding(promo))) return;
      await enforcePromoRules({ longWait: true });
      followupTimer = setTimeout(() => {
        if (!running) runEnforcementOnly();
      }, 350);
    } catch (error) {
      console.error('[CharmPromo] Promo flow failed', error);
    } finally {
      running = false;
      if (queued) {
        queued = false;
        void runPromoFlow();
      }
    }
  }
  async function runEnforcementOnly() {
    if (running) return;
    running = true;
    try {
      await enforcePromoRules();
    } catch (error) {
      console.error('[CharmPromo] Enforcement failed', error);
    } finally {
      running = false;
      if (queued) {
        queued = false;
        void runPromoFlow();
      }
    }
  }
  // Also support a promo hash clicked while already on the destination page.
  window.addEventListener('hashchange', () => {
    if (getActiveCharmLanding()) void runPromoFlow();
  });
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', runPromoFlow, { once: true });
  } else {
    void runPromoFlow();
  }
})();
